'use client';
import React, { MouseEvent, useState } from 'react';
import { IProduct } from '@mybestbrands/types';
import styles from '@/app/ui/components/product/styles.module.scss';
import Image from 'next/image';
import { useSelector } from 'react-redux';
import { appSettingsState, setVoucherData, useDispatch } from '@/app/lib/redux';
import { useTranslation } from 'next-i18next';
import ClickOutCTA from '@/app/ui/components/clickOutCTA';

interface Props {
  product: IProduct;
  locale: string;
}

const VoucherModal = (props: Props) => {
  const { t } = useTranslation(['common']);
  const { product } = props;
  const { voucherData } = useSelector(appSettingsState);

  const dispatch = useDispatch();
  const [isCopied, setIsCopied] = useState(false);

  if (!voucherData) return null;

  const voucherTitle =
    product.discountCoupon?.title || product.freeShippingCoupon?.title || product.newCustomerCoupon?.title || '';

  const voucherDescription =
    product.discountCoupon?.description ||
    product.freeShippingCoupon?.description ||
    product.newCustomerCoupon?.description ||
    '';

  const voucherCode =
    product.discountCoupon?.code || product.freeShippingCoupon?.code || product.newCustomerCoupon?.code || '';

  const voucherSubtitle = voucherCode
    ? t('common:VOUCHER_SUBTITLE').replace('##RETAILER##', product.retailerName.toUpperCase())
    : t('common:VOUCHER_SUBTITLE_WITHOUT_CODE').replace('##RETAILER##', product.retailerName.toUpperCase());

  const handleClose = (e: MouseEvent<HTMLElement>): void => {
    const clickedElement = (e.target as Element).className;
    if (clickedElement.indexOf('_close') > -1) {
      dispatch(setVoucherData(null));
      setIsCopied(false);
    }
  };

  return (
    <>
      <div
        style={{
          inset: 0,
          zIndex: 4,
          backgroundColor: 'rgba(0,0,0,0.5)',
        }}
        onClick={handleClose}
        className={'position-fixed d-flex justify-content-center align-items-center p-2 _close'}>
        <div className={`${styles.containervoucher}`}>
          <div className={'d-flex justify-content-end'}>
            <Image
              width={22}
              height={22}
              className={'pb-2 ps-2 cursor-pointer _close'}
              src="/intstatic/icons/close.svg"
              alt="close"
            />
          </div>

          <div className={'row'}>
            <div className={'col-12 px-4 d-flex justify-content-center'}>
              <div className="d-flex flex-column aligncenter gap-4">
                <strong style={{ fontSize: '1.12rem' }}>{t('common:VOUCHER_TITLE')}</strong>
                <div className={'text-center'}>{voucherSubtitle}</div>
                {voucherCode && (
                  <div className="d-flex gap-2">
                    <strong className={'align-content-center'}>{t('COMMON:VOUCHER_CODE')}</strong>
                    <div
                      className={`d-flex justify-content-center align-items-center border border-1 px-3 py-2 ${
                        isCopied ? 'fw-medium' : ''
                      }`}>
                      {voucherCode}
                    </div>
                    <div
                      onClick={() => {
                        navigator.clipboard.writeText(voucherCode);
                        setIsCopied(true);
                      }}
                      className={'mt-2 mb-2'}>
                      {isCopied ? (
                        <Image height={16} width={16} src="/intstatic/icons/check-icon.svg" alt="check" />
                      ) : (
                        <Image height={16} width={16} src="/intstatic/icons/content-copy-black.svg" alt="copy" />
                      )}
                    </div>
                  </div>
                )}

                {!voucherCode && voucherTitle && <strong style={{ fontSize: '1.12rem' }}>{voucherTitle}</strong>}

                <div className={'w-100'}>
                  <ClickOutCTA product={product} locale={props.locale}>
                    <div className={`btn btn-black-hover-light full-width pe-3`}>{t('common:USE_VOUCHER')}</div>
                  </ClickOutCTA>
                </div>

                <small className={'text-center'} style={{ fontSize: '0.75rem' }}>
                  {voucherDescription}
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VoucherModal;
